import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAxiosSwagger } from './useAxiosSwagger';
import {
    RequeueItem,
    RequeueResult,
    SyncStatusSummary,
    describeError,
    getSyncStatusBatch,
    requeueItems,
    statusKey,
} from '../api/requeue';

/**
 * The queue send-exact listens on, and therefore the queue for everything headed to Exact.
 *
 * Mind the naming: a queue is named after the SOURCE of its messages, not after the
 * destination. Queue "chargebee" holds what comes out of Chargebee and is read by
 * send-exact (app ...exsnd, TransferQueueName=chargebee). Queue "exact" is the reverse and
 * is read by send-chargebee. Putting an invoice on "exact" lands there as an
 * UnhandledEventException, which is logged at Information level without a rethrow: the
 * message then disappears without a trace and without poison.
 */
const PREFERRED_QUEUE = 'chargebee';

/**
 * How many objects per requeue call. A single request with thousands of items runs into
 * the gateway timeout and reports no progress at all while it does.
 */
const REQUEUE_CHUNK_SIZE = 250;

/** How many objects per status call. The server does one table query per object type. */
const STATUS_CHUNK_SIZE = 500;

/** How often we poll for status after a requeue before giving up (60 x 5s = 5 minutes). */
const MAX_POLLS = 60;
const POLL_INTERVAL_MS = 5000;

/** The most tracked objects we keep; enough for a backlog, not unbounded. */
const MAX_TRACKED = 10000;

export interface RequeueProgress {
    done: number;
    total: number;
}

/** An object we track until it has reached Exact. */
export interface TrackedItem {
    objectType: string;
    relationId: string;
    queuedAt: string;
}

const trackingStorageKey = (customer?: string, environment?: string) =>
    `requeue-tracked:${customer ?? ''}:${environment ?? ''}`;

const readTracked = (customer?: string, environment?: string): TrackedItem[] => {
    try {
        const raw = window.localStorage.getItem(
            trackingStorageKey(customer, environment)
        );

        return raw ? (JSON.parse(raw) as TrackedItem[]) : [];
    } catch {
        return [];
    }
};

const writeTracked = (
    customer: string | undefined,
    environment: string | undefined,
    items: TrackedItem[]
) => {
    try {
        window.localStorage.setItem(
            trackingStorageKey(customer, environment),
            JSON.stringify(items)
        );
    } catch {
        // A full or blocked localStorage must not get in the way of requeueing.
    }
};

export const useRequeue = (customer?: string, environment?: string) => {
    const axios = useAxiosSwagger();

    const [queueName, setQueueName] = useState<string>(PREFERRED_QUEUE);
    const [busy, setBusy] = useState<boolean>(false);
    const [progress, setProgress] = useState<RequeueProgress | null>(null);
    const [result, setResult] = useState<RequeueResult | null>(null);
    const [statuses, setStatuses] = useState<Record<string, SyncStatusSummary>>({});
    const [error, setError] = useState<string | null>(null);
    const [polling, setPolling] = useState<boolean>(false);
    const [checking, setChecking] = useState<boolean>(false);
    const [tracked, setTracked] = useState<TrackedItem[]>([]);

    // The polling round reads these two while running, so they have to be current
    // independently of the render. State is for display, the ref is for the logic.
    const statusesRef = useRef<Record<string, SyncStatusSummary>>({});
    const trackedRef = useRef<TrackedItem[]>([]);

    // We keep tracking what a previous session requeued: after a reload you still want to
    // see whether those messages made it into Exact.
    useEffect(() => {
        const stored = readTracked(customer, environment);

        trackedRef.current = stored;
        setTracked(stored);
        statusesRef.current = {};
        setStatuses({});
    }, [customer, environment]);

    /**
     * Pick an existing queue when the preferred one is not there.
     *
     * The list of queues comes from the live monitor, not from this hook: it reads that a
     * few times a minute anyway, and two sources for the same queue state is a guaranteed
     * way to get two different numbers on screen.
     */
    const reconcileQueueName = useCallback((available: string[]) => {
        if (available.length === 0) {
            return;
        }

        setQueueName((current) => {
            if (available.includes(current)) {
                return current;
            }

            return available.includes(PREFERRED_QUEUE)
                ? PREFERRED_QUEUE
                : available[0];
        });
    }, []);

    /** Add objects to the list we track. */
    const track = useCallback(
        (items: { objectType: string; relationId: string }[]) => {
            if (items.length === 0) {
                return;
            }

            const queuedAt = new Date().toISOString();
            const byKey = new Map<string, TrackedItem>();

            trackedRef.current.forEach((t) =>
                byKey.set(statusKey(t.objectType, t.relationId), t)
            );

            items.forEach((i) =>
                byKey.set(statusKey(i.objectType, i.relationId), {
                    objectType: i.objectType,
                    relationId: i.relationId,
                    queuedAt,
                })
            );

            // Truncate from the oldest end, so the most recent batch stays tracked.
            const merged = [...byKey.values()]
                .sort((a, b) => b.queuedAt.localeCompare(a.queuedAt))
                .slice(0, MAX_TRACKED);

            trackedRef.current = merged;
            setTracked(merged);
            writeTracked(customer, environment, merged);
        },
        [customer, environment]
    );

    /**
     * Fetch the state of the given objects in batches and update the status map. Returns
     * how many of them are not in Exact yet.
     */
    const fetchStatuses = useCallback(
        async (items: { objectType: string; relationId: string }[]) => {
            if (!axios || !customer || !environment || items.length === 0) {
                return 0;
            }

            const collected: Record<string, SyncStatusSummary> = {};

            for (let i = 0; i < items.length; i += STATUS_CHUNK_SIZE) {
                const chunk = items.slice(i, i + STATUS_CHUNK_SIZE);
                const batch = await getSyncStatusBatch(
                    axios,
                    customer,
                    environment,
                    chunk,
                    queueName
                );

                batch.items.forEach((s) => {
                    collected[statusKey(s.objectType, s.relationId)] = s;
                });
            }

            statusesRef.current = { ...statusesRef.current, ...collected };
            setStatuses(statusesRef.current);

            return Object.values(collected).filter((s) => !s.syncedToExact).length;
        },
        [axios, customer, environment, queueName]
    );

    /** Everything we track that has not reached Exact yet. */
    const pendingTracked = useCallback(
        () =>
            trackedRef.current.filter(
                (t) =>
                    !statusesRef.current[statusKey(t.objectType, t.relationId)]
                        ?.syncedToExact
            ),
        []
    );

    const requeue = useCallback(
        async (items: RequeueItem[], toQueue?: string | null) => {
            if (!axios || !customer || !environment || items.length === 0) {
                return null;
            }

            setBusy(true);
            setError(null);
            setProgress({ done: 0, total: items.length });

            // A caller that knows better than the dropdown wins. The trace works out which
            // transfer queue an object belongs on from its type, and that is knowledge a
            // selected queue name does not have: on the wrong one the handler has no case
            // for the type, so the message is deleted and the object is written into the
            // sync log as successfully synchronised.
            const target = toQueue || queueName;

            // Send in blocks: that shows progress and lets a large batch survive the
            // gateway timeout. What has already been sent stays sent when something goes
            // wrong halfway through.
            const aggregate: RequeueResult = {
                queueName: target,
                requested: 0,
                unique: 0,
                queued: 0,
                refused: 0,
                queueDepthAfter: 0,
                results: [],
            };

            const trackSent = () => {
                const sent = aggregate.results.filter((r) => r.sent);

                if (sent.length === 0) {
                    return;
                }

                track(
                    sent.map((r) => ({
                        objectType: r.objectType,
                        relationId: r.relationId,
                    }))
                );
            };

            try {
                for (let i = 0; i < items.length; i += REQUEUE_CHUNK_SIZE) {
                    const chunk = items.slice(i, i + REQUEUE_CHUNK_SIZE);
                    const response = await requeueItems(
                        axios,
                        customer,
                        environment,
                        chunk,
                        target,
                        false
                    );

                    aggregate.queueName = response.queueName;
                    aggregate.requested += response.requested;
                    aggregate.unique += response.unique;
                    aggregate.queued += response.queued;
                    aggregate.refused += response.refused ?? 0;
                    aggregate.queueDepthAfter = response.queueDepthAfter;
                    aggregate.results.push(...response.results);

                    setProgress({
                        done: Math.min(i + chunk.length, items.length),
                        total: items.length,
                    });
                    setResult({ ...aggregate, results: [...aggregate.results] });
                }

                trackSent();

                if (aggregate.queued > 0) {
                    setPolling(true);
                }

                return aggregate;
            } catch (ex) {
                setError(describeError(ex));

                // What did get sent has to stay visible, even when the rest fails.
                if (aggregate.results.length === 0) {
                    return null;
                }

                setResult({ ...aggregate, results: [...aggregate.results] });
                trackSent();

                return aggregate;
            } finally {
                setBusy(false);
                setProgress(null);
            }
        },
        [axios, customer, environment, queueName, track]
    );

    /** Fetch the state of the given objects by hand. */
    const checkStatuses = useCallback(
        async (items: { objectType: string; relationId: string }[]) => {
            if (items.length === 0) {
                return;
            }

            setChecking(true);
            setError(null);

            try {
                await fetchStatuses(items);
            } catch (ex) {
                setError(describeError(ex));
            } finally {
                setChecking(false);
            }
        },
        [fetchStatuses]
    );

    /** Fetch the state of everything requeued earlier, including a previous session. */
    const checkTracked = useCallback(
        () => checkStatuses(trackedRef.current),
        [checkStatuses]
    );

    // After a real requeue we track the items until they reach Exact. Deliberately a
    // setTimeout chain rather than setInterval: with thousands of objects a round takes
    // longer than the interval, and the calls would stack up until the browser locks up.
    useEffect(() => {
        if (!polling) {
            return;
        }

        let cancelled = false;
        let polls = 0;
        let timer: ReturnType<typeof setTimeout>;

        const run = async () => {
            if (cancelled) {
                return;
            }

            polls += 1;

            const pending = pendingTracked();

            if (pending.length === 0) {
                setPolling(false);
                return;
            }

            try {
                await fetchStatuses(pending);
            } catch (ex) {
                setError(describeError(ex));
                setPolling(false);
                return;
            }

            if (cancelled) {
                return;
            }

            if (pendingTracked().length === 0 || polls >= MAX_POLLS) {
                setPolling(false);
                return;
            }

            timer = setTimeout(run, POLL_INTERVAL_MS);
        };

        timer = setTimeout(run, POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [polling, fetchStatuses, pendingTracked]);

    const stopPolling = useCallback(() => setPolling(false), []);

    /**
     * Close only the result summary. The tracking list and the fetched statuses stay put:
     * those are precisely the answer to "did it work?".
     */
    const dismissResult = useCallback(() => {
        setResult(null);
        setError(null);
        setProgress(null);
    }, []);

    /**
     * Forget everything we were tracking, and leave the result card alone.
     *
     * Two separate things now that the tracking list is a section of its own: wiping the
     * list must not take away the report of the batch that was just sent, which is the
     * only place the per-object errors are written down.
     */
    const forgetTracked = useCallback(() => {
        setStatuses({});
        statusesRef.current = {};
        setPolling(false);
        trackedRef.current = [];
        setTracked([]);
        writeTracked(customer, environment, []);
    }, [customer, environment]);

    /**
     * The state of the tracking list.
     *
     * Deliberately three numbers instead of one: "not in Exact yet" and "not checked yet"
     * look the same on screen but mean something entirely different, and lumping them
     * together turns a fresh tracking list into a screen full of alarm.
     */
    const trackedSummary = useMemo(() => {
        let checked = 0;
        let synced = 0;

        tracked.forEach((t) => {
            const status = statuses[statusKey(t.objectType, t.relationId)];

            if (!status) {
                return;
            }

            checked += 1;

            if (status.syncedToExact) {
                synced += 1;
            }
        });

        return { total: tracked.length, checked, synced };
    }, [tracked, statuses]);

    return {
        queueName,
        setQueueName,
        reconcileQueueName,
        busy,
        progress,
        polling,
        checking,
        result,
        statuses,
        error,
        tracked,
        trackedSummary,
        requeue,
        checkStatuses,
        checkTracked,
        stopPolling,
        dismissResult,
        forgetTracked,
    };
};
